import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

/**
 * Shared parties/children/nickname editor — used by BOTH the desktop wizard
 * (app/src/components/PeoplePane.tsx) and the cloud frontend
 * (deploy/cloud-frontend/src/App.tsx ClassifyEditPanel) via the `@app/*` path
 * alias. Per the intake-unification spec, this component is **Tauri-free and
 * persistence-free**: it owns the edit UX + local cluster state and emits the
 * structured result via `onChange`; each host translates that to its own
 * meta.json write. Never import `invoke`/Tauri here.
 *
 * UX (see docs/superpowers/specs/2026-06-06-nickname-clustering-ux-spike.md +
 * docs/topics/intake-unification/nickname-clustering-mockup.html):
 *   - Each person is a card; grouping nests names as nicknames under one card.
 *   - Parties (exactly two) have LOCKED names; their nicknames are editable.
 *   - Children are cluster cards: ★ canonical + nickname chips, auto-grouped on
 *     entry. Merge two children via the "Merge with…" menu or by accepting an
 *     auto-suggestion; both are Undo-able. The × on a nickname SPLITS it back
 *     into its own child card. Click a nickname to make it the canonical name.
 *     The card ✕ ghosts the child (restorable; recorded for the audit trail).
 */

export interface EditorAlias {
  text: string;
  /** Occurrence count, when the host knows it (desktop). Cloud omits it. */
  count?: number;
}

export interface EditorPersonInput {
  name: string;
  aliases?: EditorAlias[];
  /** Occurrence count for the canonical name itself, when the host knows it
   *  (children carry a `mention_count` from the classifier). Used so a merge
   *  picks the most-frequently-used name as the master rather than going by
   *  merge order. Omit when unknown (the merge then keeps the target's name). */
  count?: number;
}

/** A host-supplied "these two children might be one person" hint. The desktop
 *  derives these from Haiku's nickname links (a nickname whose alias is also a
 *  separately-detected child); the cloud supplies none today. NO fuzzy guessing
 *  — only merges the model actually implied. */
export interface MergeSuggestion {
  /** Canonical name of the child that would fold IN. */
  child: string;
  /** Canonical name of the child it would fold INTO. */
  into: string;
  why: string;
}

export interface ExcludedItem {
  kind: "child" | "alias";
  name: string;
  /** For an excluded party alias, the party it aliased. */
  forParty?: string;
  count?: number;
}

/** How the report refers to a party. Written to `prompt_vars.pronouns_party_a/b`
 *  in the "he/him" form the engine's declared-pronouns note reads; absent =
 *  not declared, and the readability pass uses first names for that party. */
export type PronounChoice = "he/him" | "she/her" | "they/them";
export const PRONOUN_CHOICES: readonly PronounChoice[] = ["he/him", "she/her", "they/them"];

export interface PartiesChildrenEditorState {
  /** Active (non-ghosted) children, canonical names, in display order. */
  children: string[];
  /** canonical child name → its active nickname spellings. */
  childAliases: Record<string, string[]>;
  /** canonical party name → its active nickname spellings. */
  partyAliases: Record<string, string[]>;
  /** Audit trail: ghosted children + removed party aliases. */
  excluded: ExcludedItem[];
  /** canonical party name → declared pronouns. Present only when the host
   *  enabled the choice (`pronouns` prop); a party with no choice is absent. */
  partyPronouns?: Record<string, PronounChoice>;
}

/** A real message from the corpus, surfaced so the user can confirm a
 *  low-confidence nickname grouping against the actual evidence. */
export interface NameExcerpt {
  sender: string;
  date: string;
  text: string;
}

export interface PartiesChildrenEditorProps {
  /** Party names are LOCKED (from the source file); aliases are editable. */
  partyA: EditorPersonInput;
  partyB: EditorPersonInput;
  /** Children pre-grouped by the host (canonical + any high-confidence
   *  nicknames already folded in as aliases). */
  initialChildren?: EditorPersonInput[];
  /** Optional auto-merge hints rendered as dismissible banners. */
  suggestions?: MergeSuggestion[];
  /** Host-provided corpus search → a few REAL message excerpts containing
   *  `term`. When provided, low-hit nickname chips + merge suggestions get a
   *  "verify against the messages" popover (a low count is where a suggested
   *  grouping is most likely a misattribution). Omit (e.g. cloud, which has no
   *  corpus-search seam here) → the affordance is hidden entirely. */
  getExcerpts?: (term: string) => Promise<NameExcerpt[]>;
  onChange?: (state: PartiesChildrenEditorState) => void;
  /** Show a per-party pronoun choice (he/him · she/her · they/them) under
   *  each locked name. The chosen pill clicked again, or its ×, clears it.
   *  `initial` restores a prior choice on resume. Omit → no control, and
   *  the emitted state carries no `partyPronouns` key (desktop today). */
  pronouns?: { initial?: Record<string, PronounChoice> };
}

/** A nickname seen this many times or fewer gets a proactive "verify against
 *  the messages" affordance — that's where a suggested grouping is most likely
 *  a misattribution. Above it the grouping is treated as well-established.
 *  Tunable; deliberately generous (a handful of hits is still worth a glance). */
const LOW_HIT_THRESHOLD = 10;

// ── internal cluster model ────────────────────────────────────────────────
interface Spelling {
  t: string;
  count?: number;
}
interface ChildCluster {
  id: number;
  spellings: Spelling[];
  canonIdx: number;
  ghosted: boolean;
}
interface PartyState {
  /** Locked canonical name. */
  canonical: string;
  aliases: Spelling[];
  /** Aliases the user removed (audit trail). */
  excluded: Spelling[];
}

const firstWord = (s: string) => (s ?? "").trim().split(/\s+/)[0] ?? "";

function clustersFromInput(children: EditorPersonInput[], startId: number): ChildCluster[] {
  let id = startId;
  return children
    .filter((c) => (c?.name ?? "").trim())
    .map((c) => ({
      id: id++,
      canonIdx: 0,
      ghosted: false,
      spellings: [
        { t: c.name.trim(), count: c.count } as Spelling,
        ...(c.aliases ?? [])
          .map((a) => ({ t: (a.text ?? "").trim(), count: a.count }))
          .filter((a) => a.t),
      ],
    }));
}

const canonOf = (c: ChildCluster) => c.spellings[c.canonIdx]?.t ?? "";

export function PartiesChildrenEditor({
  partyA,
  partyB,
  initialChildren = [],
  suggestions = [],
  getExcerpts,
  onChange,
  pronouns: pronounsProp,
}: PartiesChildrenEditorProps) {
  const idRef = useRef(1);
  const [children, setChildren] = useState<ChildCluster[]>(() => {
    const list = clustersFromInput(initialChildren, idRef.current);
    idRef.current += list.length + 1;
    return list;
  });
  const [parties, setParties] = useState<PartyState[]>(() =>
    [partyA, partyB].map((p) => ({
      canonical: p.name,
      aliases: (p.aliases ?? [])
        .map((a) => ({ t: (a.text ?? "").trim(), count: a.count }))
        .filter((a) => a.t),
      excluded: [],
    })),
  );
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());
  const [pronouns, setPronouns] = useState<Record<string, PronounChoice>>(
    () => ({ ...(pronounsProp?.initial ?? {}) }),
  );
  const setPronoun = (canonical: string, choice: PronounChoice | undefined) =>
    setPronouns((prev) => {
      const next = { ...prev };
      if (choice) next[canonical] = choice;
      else delete next[canonical];
      return next;
    });

  // Inline-input state — only one input open across the whole editor.
  type Adding = { kind: "child"; id: number } | { kind: "party"; idx: number } | { kind: "newChild" } | null;
  const [adding, setAdding] = useState<Adding>(null);
  const [draft, setDraft] = useState("");

  // Undo buffer for the last merge/split. Snapshots BOTH the children and the
  // dismissed-suggestion set: accepting a suggestion merges AND dismisses, so
  // undo must restore both — otherwise the cards re-split but the banner stays
  // gone (it's filtered out by `dismissed`).
  const undoRef = useRef<{ children: ChildCluster[]; dismissed: Set<string> } | null>(null);
  const [undoLabel, setUndoLabel] = useState<string | null>(null);
  // The undo notice auto-dismisses after 5s (a permanent toast reads like a
  // stuck error). The undo BUFFER deliberately survives the label — clicking
  // a fresh merge re-arms both. Timer resets when a new label arrives.
  useEffect(() => {
    if (!undoLabel) return;
    const t = window.setTimeout(() => setUndoLabel(null), 5000);
    return () => window.clearTimeout(t);
  }, [undoLabel]);

  // "Verify against the messages" popover. Keyed so two chips with the same
  // text can't both open; the search term drives the lazy corpus lookup.
  const [excerptKey, setExcerptKey] = useState<string | null>(null);
  const [excerptTerm, setExcerptTerm] = useState<string>("");
  const [excerptData, setExcerptData] = useState<NameExcerpt[] | null>(null);
  const [excerptLoading, setExcerptLoading] = useState(false);
  // Which way the (absolutely-positioned) popover opens, so it can't clip off
  // the right/bottom edge of the viewport. Recomputed from its measured rect.
  const [excerptFlip, setExcerptFlip] = useState<{ x: boolean; y: boolean }>({ x: false, y: false });
  const popoverRef = useRef<HTMLDivElement | null>(null);
  // The control that opened the popover — focus returns here on close.
  const excerptTriggerRef = useRef<HTMLElement | null>(null);
  const openExcerpts = async (key: string, term: string) => {
    if (!getExcerpts) return;
    excerptTriggerRef.current =
      typeof document !== "undefined" ? (document.activeElement as HTMLElement | null) : null;
    setExcerptFlip({ x: false, y: false });
    setExcerptKey(key);
    setExcerptTerm(term);
    setExcerptData(null);
    setExcerptLoading(true);
    try {
      setExcerptData(await getExcerpts(term));
    } catch {
      setExcerptData([]);
    } finally {
      setExcerptLoading(false);
    }
  };
  const closeExcerpts = () => {
    setExcerptKey(null);
    setExcerptData(null);
    // Restore focus to whatever opened the popover (keyboard users aren't
    // stranded). Guarded — the trigger may have unmounted.
    excerptTriggerRef.current?.focus?.();
    excerptTriggerRef.current = null;
  };

  // Dismiss on Escape or an outside click while a popover is open.
  useEffect(() => {
    if (!excerptKey) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeExcerpts();
    };
    const onPointerDown = (e: MouseEvent) => {
      const el = popoverRef.current;
      if (el && !el.contains(e.target as Node)) closeExcerpts();
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onPointerDown);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onPointerDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [excerptKey]);

  // Move focus into the popover when it opens (a11y: keyboard + screen-reader
  // users land on the dialog, and the Escape/restore loop above has an anchor).
  useLayoutEffect(() => {
    if (excerptKey && popoverRef.current) popoverRef.current.focus();
  }, [excerptKey]);

  // Viewport-edge collision: after the popover lays out (and after its content
  // loads, which changes its height), flip it left/up if it would overflow.
  // jsdom returns an all-zero rect → the guard leaves it at the default anchor.
  useLayoutEffect(() => {
    const el = popoverRef.current;
    if (!excerptKey || !el || typeof window === "undefined") return;
    const r = el.getBoundingClientRect();
    if (r.width === 0 && r.height === 0) return;
    const margin = 8;
    const flipX = r.right > window.innerWidth - margin;
    const flipY = r.bottom > window.innerHeight - margin;
    setExcerptFlip((p) => (p.x === flipX && p.y === flipY ? p : { x: flipX, y: flipY }));
  }, [excerptKey, excerptData, excerptLoading]);

  const excerptPopover = (key: string) =>
    excerptKey === key ? (
      <div
        ref={popoverRef}
        className={`pce-pop${excerptFlip.x ? " pce-pop-flipx" : ""}${excerptFlip.y ? " pce-pop-flipy" : ""}`}
        role="dialog"
        aria-label={`messages mentioning ${excerptTerm}`}
        tabIndex={-1}
      >
        <div className="pce-pop-head">
          <span>“{excerptTerm}” in your messages</span>
          <button type="button" className="pce-pop-x" aria-label="close" onClick={closeExcerpts}>×</button>
        </div>
        {excerptLoading ? (
          <div className="pce-pop-empty">Loading…</div>
        ) : excerptData && excerptData.length > 0 ? (
          <ul className="pce-pop-list">
            {excerptData.slice(0, 3).map((e, i) => (
              <li key={i}>
                <div className="pce-pop-meta">{e.sender}{e.date ? ` · ${e.date}` : ""}</div>
                <div className="pce-pop-text">{e.text}</div>
              </li>
            ))}
          </ul>
        ) : (
          <div className="pce-pop-empty">No messages found for “{excerptTerm}”.</div>
        )}
      </div>
    ) : null;

  const suggestKey = (s: MergeSuggestion) => `${s.child}→${s.into}`;
  const activeSuggestions = useMemo(
    () =>
      suggestions.filter((s) => {
        if (dismissed.has(suggestKey(s))) return false;
        const namesActive = new Set(
          children.filter((c) => !c.ghosted).flatMap((c) => c.spellings.map((sp) => sp.t.toLowerCase())),
        );
        return namesActive.has(s.child.toLowerCase()) && namesActive.has(s.into.toLowerCase());
      }),
    [suggestions, dismissed, children],
  );

  // ── emit state on every change ──────────────────────────────────────────
  useEffect(() => {
    const active = children.filter((c) => !c.ghosted);
    const childAliases: Record<string, string[]> = {};
    for (const c of active) {
      const canon = canonOf(c);
      const aliases = c.spellings.filter((_, i) => i !== c.canonIdx).map((sp) => sp.t);
      if (aliases.length) childAliases[canon] = aliases;
    }
    const partyAliases: Record<string, string[]> = {};
    for (const p of parties) {
      if (p.aliases.length) partyAliases[p.canonical] = p.aliases.map((a) => a.t);
    }
    const excluded: ExcludedItem[] = [
      ...children
        .filter((c) => c.ghosted)
        .map((c) => ({ kind: "child" as const, name: canonOf(c) })),
      ...parties.flatMap((p) =>
        p.excluded.map((a) => ({
          kind: "alias" as const,
          name: a.t,
          forParty: p.canonical,
          count: a.count,
        })),
      ),
    ];
    onChange?.({
      children: active.map(canonOf),
      childAliases,
      partyAliases,
      excluded,
      ...(pronounsProp ? { partyPronouns: pronouns } : {}),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [children, parties, pronouns]);

  // ── child cluster ops ───────────────────────────────────────────────────
  const findChild = (id: number) => children.find((c) => c.id === id);
  const activeChildren = () => children.filter((c) => !c.ghosted);

  function mergeInto(aliasId: number, targetId: number) {
    if (aliasId === targetId) return;
    undoRef.current = { children, dismissed };
    // "Most-frequent name is the master": when two cards merge, the surviving
    // name is whichever of the two cards' canonical names was used more often —
    // NOT whichever card you merged into. Compares the two former canonicals
    // only (a merge chooses between the two existing names; it doesn't promote a
    // sub-nickname). Higher count wins; a tie or unknown counts keep the
    // target's name, so the result is independent of merge direction.
    const a0 = findChild(aliasId);
    const t0 = findChild(targetId);
    const aCanon = a0?.spellings[a0.canonIdx];
    const tCanon = t0?.spellings[t0.canonIdx];
    const masterName =
      aCanon && (aCanon.count ?? -1) > (tCanon?.count ?? -1) ? aCanon.t : (tCanon?.t ?? "");
    setChildren((prev) => {
      const a = prev.find((c) => c.id === aliasId);
      const t = prev.find((c) => c.id === targetId);
      if (!a || !t) return prev;
      const merged = prev
        .map((c) => {
          if (c.id !== targetId) return c;
          const spellings = [...c.spellings];
          for (const sp of a.spellings) {
            if (!spellings.some((x) => x.t.toLowerCase() === sp.t.toLowerCase())) spellings.push(sp);
          }
          const mi = spellings.findIndex((x) => x.t.toLowerCase() === masterName.toLowerCase());
          return { ...c, spellings, canonIdx: mi >= 0 ? mi : c.canonIdx };
        })
        .filter((c) => c.id !== aliasId);
      return merged;
    });
    setUndoLabel(`Merged into ${masterName}`);
  }

  function splitOut(childId: number, idx: number) {
    undoRef.current = { children, dismissed };
    setChildren((prev) => {
      const c = prev.find((x) => x.id === childId);
      if (!c || idx === c.canonIdx) return prev;
      const sp = c.spellings[idx];
      if (!sp) return prev;
      const newId = idRef.current++;
      return prev.flatMap((x) => {
        if (x.id !== childId) return [x];
        const spellings = x.spellings.filter((_, i) => i !== idx);
        const canonIdx = x.canonIdx > idx ? x.canonIdx - 1 : x.canonIdx;
        return [
          { ...x, spellings, canonIdx },
          { id: newId, spellings: [sp], canonIdx: 0, ghosted: false },
        ];
      });
    });
    setUndoLabel(`"${children.find((x) => x.id === childId)?.spellings[idx]?.t}" is now its own child`);
  }

  function undo() {
    if (!undoRef.current) return;
    setChildren(undoRef.current.children);
    setDismissed(undoRef.current.dismissed);
    undoRef.current = null;
    setUndoLabel(null);
  }

  function setCanonical(childId: number, idx: number) {
    setChildren((prev) => prev.map((c) => (c.id === childId ? { ...c, canonIdx: idx } : c)));
  }
  function ghostChild(childId: number) {
    setChildren((prev) => prev.map((c) => (c.id === childId ? { ...c, ghosted: true } : c)));
  }
  function restoreChild(childId: number) {
    setChildren((prev) => prev.map((c) => (c.id === childId ? { ...c, ghosted: false } : c)));
  }
  function addChildAlias(childId: number, text: string) {
    const t = text.trim();
    if (!t) return;
    setChildren((prev) =>
      prev.map((c) =>
        c.id === childId && !c.spellings.some((sp) => sp.t.toLowerCase() === t.toLowerCase())
          ? { ...c, spellings: [...c.spellings, { t }] }
          : c,
      ),
    );
  }
  function addNewChild(text: string) {
    const t = text.trim();
    if (!t) return;
    setChildren((prev) => [...prev, { id: idRef.current++, spellings: [{ t }], canonIdx: 0, ghosted: false }]);
  }

  // ── party alias ops ─────────────────────────────────────────────────────
  function addPartyAlias(idx: number, text: string) {
    const t = text.trim();
    if (!t) return;
    setParties((prev) =>
      prev.map((p, i) =>
        i === idx && !p.aliases.some((a) => a.t.toLowerCase() === t.toLowerCase())
          ? { ...p, aliases: [...p.aliases, { t }] }
          : p,
      ),
    );
  }
  function removePartyAlias(idx: number, aliasIdx: number) {
    setParties((prev) =>
      prev.map((p, i) => {
        if (i !== idx) return p;
        const removed = p.aliases[aliasIdx];
        return {
          ...p,
          aliases: p.aliases.filter((_, j) => j !== aliasIdx),
          excluded: removed ? [...p.excluded, removed] : p.excluded,
        };
      }),
    );
  }

  const openInput = (a: Adding) => {
    setAdding(a);
    setDraft("");
  };
  const commitDraft = () => {
    if (!adding) return;
    if (adding.kind === "child") addChildAlias(adding.id, draft);
    else if (adding.kind === "party") addPartyAlias(adding.idx, draft);
    else if (adding.kind === "newChild") addNewChild(draft);
    setAdding(null);
    setDraft("");
  };
  const cancelDraft = () => {
    setAdding(null);
    setDraft("");
  };

  const inputFor = (open: boolean, placeholder: string, ariaLabel: string) =>
    open ? (
      <span className="pce-input">
        <input
          type="text"
          autoFocus
          aria-label={ariaLabel}
          placeholder={placeholder}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") commitDraft();
            if (e.key === "Escape") cancelDraft();
          }}
        />
        <button type="button" className="pce-ok" aria-label="confirm" onClick={commitDraft}>✓</button>
        <button type="button" className="pce-cancel" aria-label="cancel" onClick={cancelDraft}>×</button>
      </span>
    ) : null;

  return (
    <div className="pce">
      <div className="pce-section-label">The two people in the conversation</div>
      {pronounsProp && (
        <div className="pce-hint">
          Pronouns are optional. Choose one and the report uses it; leave it and the report uses first names.
        </div>
      )}
      <div className="pce-cards" data-testid="pce-parties">
        {parties.map((p, idx) => (
          <div className="pce-card pce-party" key={`party-${idx}`} data-testid={`pce-party-${idx}`}>
            <div className="pce-role">Party {idx + 1}</div>
            <div className="pce-name">{p.canonical}</div>
            <div className="pce-sub pce-lock">name from your file — locked</div>
            {pronounsProp && (
              <div className="pce-pron">
                <span className="pce-pron-label" id={`pce-pron-label-${idx}`}>
                  Refer to {p.canonical} as
                </span>
                <span className="pce-seg" role="radiogroup" aria-labelledby={`pce-pron-label-${idx}`}>
                  {PRONOUN_CHOICES.map((choice) => {
                    const on = pronouns[p.canonical] === choice;
                    return (
                      <label key={choice}>
                        <input
                          type="radio"
                          name={`pce-pron-${idx}`}
                          value={choice}
                          checked={on}
                          aria-label={`${choice} for ${p.canonical}`}
                          onChange={() => setPronoun(p.canonical, choice)}
                          // A radio group cannot otherwise be unset: the chosen
                          // pill clicked again clears it (onChange stays silent
                          // for an already-checked radio, so only this fires).
                          onClick={() => { if (on) setPronoun(p.canonical, undefined); }}
                        />
                        <span>{choice}</span>
                      </label>
                    );
                  })}
                </span>
                {pronouns[p.canonical] && (
                  <button
                    type="button"
                    className="pce-ico pce-pron-clear"
                    aria-label={`clear pronouns for ${p.canonical}`}
                    title="Clear — the report uses the first name"
                    onClick={() => setPronoun(p.canonical, undefined)}
                  >
                    ×
                  </button>
                )}
              </div>
            )}
            <div className="pce-chips">
              <span className="pce-chiplabel">also called</span>
              {p.aliases.map((a, ai) => (
                <span className="pce-chip" key={`pa-${idx}-${a.t}`}>
                  <span className="pce-nm">{a.t}</span>
                  {a.count != null &&
                    (getExcerpts && a.count <= LOW_HIT_THRESHOLD ? (
                      <button
                        type="button"
                        className="pce-ct pce-verify"
                        title={`Read the ${a.count} message${a.count === 1 ? "" : "s"} that mention ${a.t}`}
                        aria-label={`see messages mentioning ${a.t}`}
                        onClick={() => openExcerpts(`party-${idx}-${ai}`, a.t)}
                      >
                        {a.count} message{a.count === 1 ? "" : "s"}
                      </button>
                    ) : (
                      <span className="pce-ct">{a.count}</span>
                    ))}
                  <button
                    type="button"
                    className="pce-ico pce-rm"
                    aria-label={`remove nickname ${a.t} for ${p.canonical}`}
                    title="Remove this nickname"
                    onClick={() => removePartyAlias(idx, ai)}
                  >
                    ×
                  </button>
                  {excerptPopover(`party-${idx}-${ai}`)}
                </span>
              ))}
              {adding?.kind === "party" && adding.idx === idx
                ? inputFor(true, "nickname…", `New nickname for ${p.canonical}`)
                : (
                  <button
                    type="button"
                    className="pce-btn pce-tiny"
                    aria-label={`add nickname for ${p.canonical}`}
                    onClick={() => openInput({ kind: "party", idx })}
                  >
                    + nickname
                  </button>
                )}
            </div>
          </div>
        ))}
      </div>

      <div className="pce-children-head">
        <div className="pce-section-label">Children</div>
        <div className="pce-children-head-actions">
          {undoLabel && (
            <div className="pce-undo" role="status">
              <span>{undoLabel}</span>
              <button type="button" className="pce-btn pce-tiny" onClick={undo}>
                Undo
              </button>
            </div>
          )}
          {adding?.kind === "newChild"
            ? inputFor(true, "child's first name…", "Add a child")
            : (
              <button
                type="button"
                className="pce-btn"
                onClick={() => openInput({ kind: "newChild" })}
              >
                + add a child
              </button>
            )}
        </div>
      </div>

      {activeSuggestions.map((s) => (
        <div className="pce-suggest" key={suggestKey(s)} data-testid="pce-suggest">
          <div className="pce-suggest-title">These might be the same child</div>
          <div className="pce-suggest-body">
            <strong>{s.child}</strong> and <strong>{s.into}</strong> could be one person.
          </div>
          <div className="pce-suggest-why">{s.why}</div>
          <div className="pce-suggest-row">
            <button
              type="button"
              className="pce-btn pce-solid pce-tiny"
              onClick={() => {
                const a = activeChildren().find((c) => canonOf(c).toLowerCase() === s.child.toLowerCase());
                const t = activeChildren().find((c) => canonOf(c).toLowerCase() === s.into.toLowerCase());
                if (a && t) mergeInto(a.id, t.id);
                setDismissed((d) => new Set(d).add(suggestKey(s)));
              }}
            >
              Yes — they're one child
            </button>
            <button
              type="button"
              className="pce-btn pce-tiny"
              onClick={() => setDismissed((d) => new Set(d).add(suggestKey(s)))}
            >
              No — keep separate
            </button>
            {getExcerpts && (
              <button
                type="button"
                className="pce-btn pce-tiny pce-link"
                onClick={() => openExcerpts(`sugg-${suggestKey(s)}`, s.child)}
              >
                Show the messages
              </button>
            )}
          </div>
          {excerptPopover(`sugg-${suggestKey(s)}`)}
        </div>
      ))}

      <div className="pce-cards" data-testid="pce-children">
        {children.length === 0 && <div className="pce-empty">No children in this case.</div>}
        {children.map((c) => {
          const canon = canonOf(c);
          const others = activeChildren().filter((o) => o.id !== c.id);
          if (c.ghosted) {
            return (
              <div className="pce-card pce-ghosted" key={c.id} data-testid={`pce-card-${canon}`}>
                <div className="pce-card-head">
                  <div className="pce-title">
                    <div className="pce-name">{canon}</div>
                    <div className="pce-sub">removed from this case</div>
                  </div>
                  <button type="button" className="pce-btn pce-tiny" onClick={() => restoreChild(c.id)}>
                    Restore
                  </button>
                </div>
              </div>
            );
          }
          return (
            <div className="pce-card" key={c.id} data-testid={`pce-card-${canon}`}>
              <div className="pce-card-head">
                <div className="pce-title">
                  <div className="pce-name">{canon}</div>
                  <div className="pce-sub">in your messages</div>
                </div>
                <button
                  type="button"
                  className="pce-iconbtn"
                  title="Not a child / remove"
                  aria-label={`remove child ${canon}`}
                  onClick={() => ghostChild(c.id)}
                >
                  ✕
                </button>
              </div>
              <div className="pce-chips">
                <span className="pce-chiplabel">also called</span>
                {c.spellings.map((sp, i) => {
                  const isCanon = i === c.canonIdx;
                  // The canonical spelling is already the card title — don't also
                  // list it as an "also called" chip (it reads as "X is also
                  // called X", worst for a child with no real nicknames). Matches
                  // the party cards, where the name is the title and only real
                  // aliases appear as chips. Promote a nickname by clicking it.
                  if (isCanon) return null;
                  return (
                    <span className={`pce-chip${isCanon ? " pce-canon" : ""}`} key={`${c.id}-${sp.t}-${i}`}>
                      {isCanon && <span className="pce-star">★</span>}
                      <span
                        className="pce-nm"
                        role="button"
                        tabIndex={0}
                        title={isCanon ? "This is the name the analysis uses" : "Use this spelling as the name"}
                        onClick={() => setCanonical(c.id, i)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") setCanonical(c.id, i);
                        }}
                      >
                        {sp.t}
                      </span>
                      {sp.count != null &&
                        (!isCanon && getExcerpts && sp.count <= LOW_HIT_THRESHOLD ? (
                          <button
                            type="button"
                            className="pce-ct pce-verify"
                            title={`Read the ${sp.count} message${sp.count === 1 ? "" : "s"} that mention ${sp.t}`}
                            aria-label={`see messages mentioning ${sp.t}`}
                            onClick={() => openExcerpts(`chip-${c.id}-${i}`, sp.t)}
                          >
                            {sp.count} message{sp.count === 1 ? "" : "s"}
                          </button>
                        ) : (
                          <span className="pce-ct">{sp.count}</span>
                        ))}
                      {!isCanon && (
                        <button
                          type="button"
                          className="pce-ico pce-rm"
                          title="Not the same person — pop out into its own child"
                          aria-label={`split nickname ${sp.t} out of ${canon}`}
                          onClick={() => splitOut(c.id, i)}
                        >
                          ×
                        </button>
                      )}
                      {excerptPopover(`chip-${c.id}-${i}`)}
                    </span>
                  );
                })}
                {adding?.kind === "child" && adding.id === c.id
                  ? inputFor(true, "another nickname…", `New nickname for ${canon}`)
                  : (
                    <button
                      type="button"
                      className="pce-btn pce-tiny"
                      aria-label={`add nickname for ${canon}`}
                      onClick={() => openInput({ kind: "child", id: c.id })}
                    >
                      + nickname
                    </button>
                  )}
              </div>
              {others.length > 0 && (
                <div className="pce-card-foot">
                  <select
                    aria-label={`Merge ${canon} with another child`}
                    value=""
                    onChange={(e) => {
                      const targetId = Number(e.target.value);
                      if (targetId) mergeInto(c.id, targetId);
                    }}
                  >
                    <option value="">Merge with…</option>
                    {others.map((o) => (
                      <option key={o.id} value={o.id}>
                        {canonOf(o)}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          );
        })}
      </div>

    </div>
  );
}

/** Derive host-supplied merge suggestions from detected children + nicknames:
 *  a nickname whose `alias` is ALSO a separately-detected child name implies
 *  those two children are one person. Normalized-equality only — NO fuzzy
 *  matching (per the spike decision). Returns at most one suggestion per
 *  duplicate pair. `childNames` and `nicknames` are the host's detected data. */
export function computeMergeSuggestions(
  childNames: string[],
  nicknames: { alias: string; canonical: string }[],
): MergeSuggestion[] {
  const norm = (s: string) => firstWord(s).toLowerCase();
  const byNorm = new Map<string, string>();
  for (const n of childNames) {
    const k = norm(n);
    if (k) byNorm.set(k, n);
  }
  const out: MergeSuggestion[] = [];
  const seen = new Set<string>();
  for (const nk of nicknames) {
    const aliasChild = byNorm.get(norm(nk.alias));
    const canonChild = byNorm.get(norm(nk.canonical));
    // Both the alias and its canonical surfaced as their own children → dupe.
    if (aliasChild && canonChild && norm(nk.alias) !== norm(nk.canonical)) {
      const key = `${aliasChild}→${canonChild}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({
        child: aliasChild,
        into: canonChild,
        why: `Aurora linked "${nk.alias} → ${nk.canonical}", and the analysis saw "${aliasChild}" used the same way.`,
      });
    }
  }
  return out;
}
